import { Log, type Profile, type SigninRequest, User, UserManager, WebStorageStateStore } from 'oidc-client';
import { IDENTITY_CONFIG, METADATA_CONFIG } from '../helpers/auth-settings';
import { DEFAULT_IDP } from '../helpers/constants';

Log.logger = console;
Log.level = Log.DEBUG;

const AUTH_CONFIG_KEY = 'authConfig';

export interface AuthConfig {
  logoutUrl?: string;
  frontendSystem?: string;
}

export default class AuthService {
  private static instance: AuthService | null = null;

  private UserManager: UserManager | undefined;
  private authConfig: AuthConfig = {};

  constructor(args = { idp: DEFAULT_IDP }) {
    if (AuthService.instance) {
      return AuthService.instance;
    }

    this.UserManager = new UserManager({
      ...IDENTITY_CONFIG,
      userStore: new WebStorageStateStore({ store: window.localStorage }),
      metadata: {
        ...METADATA_CONFIG,
      },
      extraQueryParams: { identity_provider: args.idp },
    });

    Log.logger = console;
    Log.level = Log.DEBUG;

    this.UserManager.events.addSilentRenewError((e) => {
      console.log('silent renew error', e.message);
    });
    this.UserManager.events.addAccessTokenExpired(() => {
      console.log('token expired');
      this.signinSilent();
    });

    // restore authConfig if it exists
    const storedConfig = localStorage.getItem(AUTH_CONFIG_KEY);
    if (storedConfig) {
      try {
        this.authConfig = JSON.parse(storedConfig);
      } catch {
        this.authConfig = {};
      }
    }

    AuthService.instance = this;
    return this;
  }

  signinRedirectCallback = (): Promise<User> => {
    return this.UserManager!.signinRedirectCallback();
  };

  getUser = async (): Promise<User> => {
    const user = await this.UserManager!.getUser();
    if (!user) {
      return await this.UserManager!.signinRedirectCallback();
    }
    return user;
  };

  signinRedirect = (idp?: string, redirectQueryParams?: unknown): Promise<void> => {
    if (redirectQueryParams) {
      localStorage.setItem('redirectQueryParams', JSON.stringify(redirectQueryParams));
    } else {
      // remove old query params if none added to signin request
      localStorage.removeItem('redirectQueryParams');
      localStorage.setItem('redirectPath', window.location.pathname);
    }

    // add extraQueryParams to signinRedirect, default is identity_provider myosh-default if any other remove identity_provider and set the domain as idp_identifier.
    let args: { extraQueryParams: { identity_provider?: string; idp_identifier?: string } } = {
      extraQueryParams: { identity_provider: DEFAULT_IDP },
    };

    if (idp !== DEFAULT_IDP) {
      args = { extraQueryParams: { idp_identifier: idp } };
    }

    return this.UserManager!.signinRedirect(args);
  };

  /**
   * Stores the user's subject identifier (sub claim) in localStorage.
   * @param profile - The user's OIDC profile containing the sub claim.
   */
  setUser = (profile: Pick<Profile, 'sub'>) => {
    return localStorage.setItem('userId', profile.sub);
  };

  signinSilent = async (): Promise<User> => {
    return this.UserManager!.signinSilent()
      .then((user) => {
        // console.log('signed in', user);
        return user;
      })
      .catch((err) => {
        // console.log(err);
        return err;
      });
  };

  signinSilentCallback = () => {
    return this.UserManager!.signinSilentCallback();
  };

  createSigninRequest = (): Promise<SigninRequest> => {
    return this.UserManager!.createSigninRequest();
  };

  logout = () => {
    return this.UserManager!.signoutRedirect({
      extraQueryParams: {
        client_id: IDENTITY_CONFIG.client_id,
        logout_uri: IDENTITY_CONFIG.post_logout_redirect_uri,
      },
    });
  };

  signoutRedirectCallback = (isDefaultIdp?: boolean) => {
    const { logoutUrl, frontendSystem } = this.authConfig;

    this.UserManager!.clearStaleState();
    return this.UserManager!.signoutRedirectCallback().then(() => {
      // clear all potentially locally stored items
      localStorage.clear();

      // default redirect url
      let url = '/login';

      if (frontendSystem !== 'odin') {
        // keep existing behaviour for non 'odin' frontend systems
        const configuredLogoutUrl = process.env.REACT_APP_AUTH_MYOSH_IDP_LOGOUT_URL;
        if (isDefaultIdp === true && configuredLogoutUrl) {
          // if the user is using the default idp, use the provided logout url
          url = configuredLogoutUrl;
        } else if (isDefaultIdp === false && logoutUrl) {
          // if the user is not using the default idp, use the set logout url
          url = logoutUrl;
        }
      }

      window.location.replace(url);
    });
  };

  /**
   * Set the instance to null.
   * This method is used to reset the singleton instance of AuthService.
   * It can be useful for testing purposes or when you want to create a new instance.
   * @private
   * @static
   * @returns {void}
   */
  static _resetInstance(): void {
    AuthService.instance = null;
  }

  getAuthConfig = (): AuthConfig => {
    return this.authConfig;
  };

  setAuthConfig = (config: Partial<AuthConfig>): void => {
    this.authConfig = { ...this.authConfig, ...config };
    localStorage.setItem(AUTH_CONFIG_KEY, JSON.stringify(this.authConfig));
  };
}
